import { createContext, useContext, useState, useCallback, useRef, type ReactNode } from 'react';

export type FileData = {
  content: string;
  encoding?: string;
  created_at: string;
  modified_at: string;
};

export type FilesMap = Record<string, FileData>;

type FilesContextValue = {
  files: FilesMap;
  selectedFile: string | null;
  setSelectedFile: (path: string | null) => void;
  /** Reconcile server files with local dirty edits. */
  setFiles: (files: FilesMap) => void;
  /** Hard reset: clear all files AND dirty state (use on thread/route switch). */
  clearFiles: () => void;
  /** Update a single file's content (marks it dirty for next agent invocation). */
  updateFile: (path: string, content: string) => void;
  /** Take and clear all dirty files. Call this when sending a message. */
  takeDirtyFiles: () => FilesMap | null;
};

const FilesContext = createContext<FilesContextValue | null>(null);

export function FilesProvider({ children }: { children: ReactNode }) {
  const [files, setFiles] = useState<FilesMap>({});
  const [selectedFile, setSelectedFile] = useState<string | null>(null);
  const dirtyRef = useRef<FilesMap>({});

  const setFilesFromServer = useCallback((newFiles: FilesMap) => {
    // Reconcile: clear dirty entries where server content now matches
    const nextDirty: FilesMap = {};
    for (const [path, dirtyFile] of Object.entries(dirtyRef.current)) {
      const serverFile = newFiles[path];
      if (!serverFile || (serverFile.content ?? '') !== (dirtyFile.content ?? '')) {
        nextDirty[path] = dirtyFile;
      }
    }
    dirtyRef.current = nextDirty;

    // Merge: server state as base, pending user edits on top
    const merged = Object.keys(nextDirty).length > 0 ? { ...newFiles, ...nextDirty } : newFiles;

    setFiles(merged);
    setSelectedFile((prev) => {
      if (prev && merged[prev] && prev.startsWith('/shared/')) return prev;
      const firstVisiblePath = Object.keys(merged).find((path) => path.startsWith('/shared/'));
      return firstVisiblePath ?? null;
    });
  }, []);

  const clearFiles = useCallback(() => {
    dirtyRef.current = {};
    setFiles({});
    setSelectedFile(null);
  }, []);

  const updateFile = useCallback((path: string, content: string) => {
    setFiles((prev) => {
      const existing = prev[path];
      if (!existing) return prev;
      const now = new Date().toISOString();
      const updated: FileData = { ...existing, content, modified_at: now };
      dirtyRef.current[path] = updated;
      return { ...prev, [path]: updated };
    });
  }, []);

  const takeDirtyFiles = useCallback((): FilesMap | null => {
    const dirty = dirtyRef.current;
    if (Object.keys(dirty).length === 0) return null;
    // Return a copy — don't clear yet. Dirty entries persist until the
    // server state confirms the edit (reconciled in setFilesFromServer).
    return { ...dirty };
  }, []);

  return (
    <FilesContext.Provider
      value={{
        files,
        selectedFile,
        setSelectedFile,
        setFiles: setFilesFromServer,
        clearFiles,
        updateFile,
        takeDirtyFiles,
      }}
    >
      {children}
    </FilesContext.Provider>
  );
}

export function useFiles() {
  const ctx = useContext(FilesContext);
  if (!ctx) throw new Error('useFiles must be used within a FilesProvider');
  return ctx;
}
